import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { getAllPlans, createPlan, updatePlan, deletePlan } from "../../api/setupApi";

const TIER_OPTIONS = [
  { label: "Free", value: "free" },
  { label: "Basic", value: "basic" },
  { label: "Pro", value: "pro" },
  { label: "Enterprise", value: "enterprise" },
];

const emptyPlan = {
  name: "",
  description: "",
  tier: "basic",
  monthlyPrice: 0,
  annualPrice: 0,
  maxStudents: 0,
  maxStaff: 0,
  maxParents: 0,
  isActive: true,
  isDefault: false,
};

const PlanManagement = () => {
  const toast = useRef(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({ ...emptyPlan });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const result = await getAllPlans();
      if (result.success) setPlans(result.data);
    } catch (err) {
      console.error("Error fetching plans:", err);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingPlan(null);
    setFormData({ ...emptyPlan });
    setDialogVisible(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description || "",
      tier: plan.tier,
      monthlyPrice: plan.monthlyPrice,
      annualPrice: plan.annualPrice,
      maxStudents: plan.maxStudents,
      maxStaff: plan.maxStaff,
      maxParents: plan.maxParents,
      isActive: plan.isActive,
      isDefault: plan.isDefault,
    });
    setDialogVisible(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.tier) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Name and tier are required" });
      return;
    }
    setSaving(true);
    try {
      if (editingPlan) {
        await updatePlan(editingPlan.id, formData);
        toast.current?.show({ severity: "success", summary: "Updated", detail: "Plan updated successfully" });
      } else {
        await createPlan(formData);
        toast.current?.show({ severity: "success", summary: "Created", detail: "Plan created successfully" });
      }
      setDialogVisible(false);
      fetchPlans();
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: err.response?.data?.message || "Failed to save plan" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    try {
      await deletePlan(plan.id);
      toast.current?.show({ severity: "success", summary: "Deactivated", detail: "Plan deactivated" });
      fetchPlans();
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: err.response?.data?.message || "Failed to deactivate plan" });
    }
  };

  const priceTemplate = (rowData, field) => {
    const cents = rowData[field];
    return cents === 0 ? "Free" : `$${(cents / 100).toFixed(0)}`;
  };

  const tierTemplate = (rowData) => {
    const colors = { free: "success", basic: "info", pro: "warning", enterprise: "danger" };
    return <Tag value={rowData.tier} severity={colors[rowData.tier]} />;
  };

  const statusTemplate = (rowData) => (
    <Tag value={rowData.isActive ? "Active" : "Inactive"} severity={rowData.isActive ? "success" : "danger"} />
  );

  const limitTemplate = (value) => (value === 0 ? "Unlimited" : value);

  const actionTemplate = (rowData) => (
    <div className="flex gap-2">
      <Button icon="pi pi-pencil" className="p-button-sm p-button-text" onClick={() => openEdit(rowData)} />
      {!rowData.isDefault && rowData.isActive && (
        <Button icon="pi pi-trash" className="p-button-sm p-button-text p-button-danger" onClick={() => handleDelete(rowData)} />
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: "60vh" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-3">
      <Toast ref={toast} />

      <div className="flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="m-0">Subscription Plans</h2>
          <p className="text-500 mt-1 mb-0">Manage pricing tiers for school subscriptions</p>
        </div>
        <Button label="New Plan" icon="pi pi-plus" onClick={openNew} />
      </div>

      <div className="card">
        <DataTable value={plans} className="p-datatable-sm" emptyMessage="No plans found.">
          <Column field="name" header="Plan Name" sortable />
          <Column field="tier" header="Tier" body={tierTemplate} sortable />
          <Column header="Monthly" body={(row) => priceTemplate(row, "monthlyPrice")} />
          <Column header="Annual" body={(row) => priceTemplate(row, "annualPrice")} />
          <Column header="Students" body={(row) => limitTemplate(row.maxStudents)} />
          <Column header="Staff" body={(row) => limitTemplate(row.maxStaff)} />
          <Column field="isActive" header="Status" body={statusTemplate} />
          <Column header="Actions" body={actionTemplate} style={{ width: "8rem" }} />
        </DataTable>
      </div>

      <Dialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        header={editingPlan ? "Edit Plan" : "Create Plan"}
        modal
        style={{ width: "500px" }}
      >
        <div className="flex flex-column gap-3">
          <div className="p-field">
            <label className="font-semibold block mb-1">Name</label>
            <InputText value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full" />
          </div>
          <div className="p-field">
            <label className="font-semibold block mb-1">Description</label>
            <InputTextarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full" rows={2} />
          </div>
          <div className="p-field">
            <label className="font-semibold block mb-1">Tier</label>
            <Dropdown value={formData.tier} options={TIER_OPTIONS} onChange={(e) => setFormData({ ...formData, tier: e.value })} className="w-full" />
          </div>
          <div className="grid">
            <div className="col-6">
              <label className="font-semibold block mb-1">Monthly Price (cents)</label>
              <InputNumber value={formData.monthlyPrice} onValueChange={(e) => setFormData({ ...formData, monthlyPrice: e.value || 0 })} className="w-full" />
            </div>
            <div className="col-6">
              <label className="font-semibold block mb-1">Annual Price (cents)</label>
              <InputNumber value={formData.annualPrice} onValueChange={(e) => setFormData({ ...formData, annualPrice: e.value || 0 })} className="w-full" />
            </div>
          </div>
          <div className="grid">
            <div className="col-4">
              <label className="font-semibold block mb-1">Max Students</label>
              <InputNumber value={formData.maxStudents} onValueChange={(e) => setFormData({ ...formData, maxStudents: e.value || 0 })} className="w-full" min={0} />
            </div>
            <div className="col-4">
              <label className="font-semibold block mb-1">Max Staff</label>
              <InputNumber value={formData.maxStaff} onValueChange={(e) => setFormData({ ...formData, maxStaff: e.value || 0 })} className="w-full" min={0} />
            </div>
            <div className="col-4">
              <label className="font-semibold block mb-1">Max Parents</label>
              <InputNumber value={formData.maxParents} onValueChange={(e) => setFormData({ ...formData, maxParents: e.value || 0 })} className="w-full" min={0} />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex align-items-center gap-2">
              <InputSwitch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.value })} />
              <label>Active</label>
            </div>
            <div className="flex align-items-center gap-2">
              <InputSwitch checked={formData.isDefault} onChange={(e) => setFormData({ ...formData, isDefault: e.value })} />
              <label>Default</label>
            </div>
          </div>
          <div className="flex justify-content-end gap-2 mt-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setDialogVisible(false)} />
            <Button label={saving ? "Saving..." : "Save"} onClick={handleSave} disabled={saving} loading={saving} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PlanManagement;
